import React, { useEffect, useState, useRef, forwardRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Col, Row, Button, Alert, Spinner, Tabs, Tab } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import API from "../../Utility/Api";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import MultiSelectTypeahead from "../../MultiSelectTypeahead/MultiSelectTypeahead";
import SortableMilestones from './SortableMilestones';
import { FormattedTextEditor } from "../../FormattedText/FormattedTextEditor";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import {
  FaRegTrashAlt
} from "react-icons/fa";
import ErrorModal from "../../ErrorModal/ErrorModal";

// Styles
import "../WorkflowView.scss";
import { isEmpty, set } from 'draft-js/lib/DefaultDraftBlockRenderMap';
import { v4 as uuidv4 } from 'uuid';

export default function EditWorkflowTabView({ workflow, action }) {
  let { project, workflowId } = useParams();
  let history = useHistory();

  let newTask = {
    name: '',
    details: '',
    instructions: '',
    assigneeRoles: [],
    userCanComplete: true
  };

  let newMilestone = {
    name: '',
    startDay: '',
    endDay: ''
  };

  const formRef = useRef();
  const milestoneFormRef = useRef();
  const taskFormRef = useRef();

  const [selectedMilestone, setSelectedMilestone] = useState({ milestone: newMilestone });
  const [selectedTask, setSelectedTask] = useState({ task: newTask });
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);
  const [milestones, setMilestones] = useState(workflow.milestones || []);
  const [formValues, setFormValues] = useState(selectedTask.task);
  const [milestoneFormValues, setMilestoneFormValues] = useState(selectedMilestone.milestone);
  const [settingNewSelectedTask, setSettingNewSelectedTask] = useState(false);
  const [assigneeRoles, setAssigneeRoles] = useState(workflow.assigneeRoles || []);
  const [removedAssigneeRoles, setRemovedAssigneeRoles] = useState([]);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [deleteMilestoneModalShow, setDeleteMilestoneModalShow] = useState(false);
  const [disableAssigneesField, setDisableAssigneesField] = useState(true);
  const [showDeleteAssigneeRoleError, setShowDeleteAssigneeRoleError] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const redirectOnSuccess = () => {
    setSubmittingForm(false);
    setTimeout(() => {
      // history.push({ pathname: `/workflows/${project}/${workflowId}` });
      setSubmitSuccess(null);
    }, 1000);
  };

  const upsertWorkflow = async(values, setFieldValue) => {
    try {
      values.action = action;
      values._id = workflow._id;
      if (!values.name) {
        throw new Error(`Workflow needs to have a name`);
      }

      if (assigneeRoles?.length > 0) {
        values.assigneeRoles = assigneeRoles;
        assigneeRoles.forEach((role) => {
          if (!role?.name?.length) {
            throw new Error(`Roles need to have names`);
          }
          if (role?.tempId) {
            delete role.tempId;
            delete role._id;
          }
        });
      }

      if (values?.milestones?.items) {
        values.milestones = values.milestones.items;
      }

      if (values?.milestones?.length > 0) {
        values.milestones.forEach((milestone) => {
          if (!milestone.name) {
            throw new Error(`Milestones need to have names`);
          }
          if (!milestone.startDay) {
            throw new Error(`Milestone "${milestone.name}" needs a start day`);
          }
          if (!milestone.endDay) {
            throw new Error(`Milestone "${milestone.name}" needs an end day`);
          }
          if (milestone.tasks?.length > 0) {
            milestone.tasks.forEach((task) => {
              if (!task.name) {
                throw new Error(`Task(s) in milestone "${milestone.name}" need to have names`);
              }
              // if (task.userCanComplete !== 'false' || task.userCanComplete !== 'true' || task.userCanComplete !== false || task.userCanComplete !== true) {
              //   throw new Error(`Task "${task.name}" in milestone "${milestone.name} needs to have a valid completion method`);
              // }
              if (task.type !== 'message' && (!task.userCanComplete || task.userCanComplete === 'false' || task.userCanComplete === false) && (!task.assigneeRoles?.length || task.assigneeRoles?.length === 0)) {
                throw new Error(`Task "${task.name}" in milestone "${milestone.name} needs to have at least one assignee`);
              }
              if (task.userCanComplete === true || task.userCanComplete === 'true' || task.type === 'message') {
                task.assigneeRoles = [];
              }
            });
          }
        });
      }

      if (removedAssigneeRoles?.length > 0) {
        values.removedAssigneeRoles = removedAssigneeRoles;
      }

      const apiCallConfig = {
        // url: `/admin/${project}/workflows/${action === 'update' ? workflow._id + '/' : ''}${action}`,
        url: `/admin/${project}/workflows/${workflow._id}`,
        data: values,
      };
      setSubmittingForm(true);
      let response;
      if (action === 'update') {
        response = await API.put(apiCallConfig);
      } else {
        response = await API.post(apiCallConfig);
      }
      setMilestones(response.milestones || []);
      setAssigneeRoles(response.assigneeRoles || []);
      setRemovedAssigneeRoles([]);
      if (response) {
        if (response._id) {
          if (!workflowId && response._id) {
            workflowId = response._id;
          }
          if (action === "update") {
            // success
            setSubmitSuccess("Workflow updated successfully");
            redirectOnSuccess();
          } else if (action === "create") {
            setSubmitSuccess("Workflow created successfully");
            redirectOnSuccess();
          } else if (response.message) {
            setSubmitError(response.message);
          } else {
            setSubmitError("Something went wrong");
          }
        } else {
          setSubmitError("Something went wrong");
        }
        setSubmittingForm(false);
      }

    } catch (err) {
      console.log(err);
      if (err.response) {
        setSubmitError(err.response.data.message);
      } else {
        setSubmitError(err.message || "An error occurred, please try again");
      }
      setSubmittingForm(false);
    }
  };

  const handleSubmit = async(values, setFieldValue) => {
    values.milestones = milestones;
    values.assigneeRoles = assigneeRoles;
    setSubmitSuccess(null);
    setSubmitError(null);
    await upsertWorkflow(values, setFieldValue);
  };

  const handleCancel = (event) => {
    // window.location.reload();
    setSubmitError(null);
    history.push({ pathname: `/workflows/${project}/${workflowId}` });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(3).required("Required"),
    status: Yup.string().min(3).required("Required"),
    assigneeRoles: Yup.array()
      .of(Yup.object({ name: Yup.string(), _id: Yup.string() }))
      .min(1, `You need to assign at least one role`)
      .required('Required')
  });

  const milestoneValidationSchema = Yup.object().shape({
    name: Yup.string().min(3).required("Required"),
    startDay: Yup.number().min(1).required("Required"),
    endDay: Yup.number().min(1).required("Required")
      .when(['startDay'], (startDay, schema) => {
        return schema.min(startDay);
      })
  });

  const onMilestonesChange = (milestones) => {
    setMilestones(milestones.items || milestones);
    setSubmitError(null);
  };

  const handleChange = (e) => {
    // console.log(e);
    setSubmitError(null);
  };

  const handleTaskSubmit = async(values, setFieldValue) => {};

  const handleTaskChange = (e) => {
    if (selectedTask?.milestoneIndex >= 0 && selectedTask?.taskIndex >= 0) {
      selectedTask.task[e.target.name] = e.target.value;
      const newState = [...milestones];
      newState[selectedTask.milestoneIndex].tasks[selectedTask.taskIndex][e.target.name] = e.target.value;
      setMilestones(newState);
      setSubmitError(null);
    }
  };

  const handleMilestoneChange = (e) => {
    if (selectedMilestone?.milestoneIndex >= 0) {
      const newState = [...milestones];
      newState[selectedMilestone.milestoneIndex][e.target.name] = e.target.value;
      setMilestones(newState);
      setSubmitError(null);
    }
  };

  useEffect(() => {
    onMilestonesChange(milestones);
  }, [milestones]);

  useEffect(() => {
    if (selectedTask?.task && !selectedTask?.task?.type) {
      selectedTask.task.type = "simple";
    }
    setFormValues(selectedTask.task);
  }, [selectedTask]);

  const onSelectedItemChange = (item) => {
    setSettingNewSelectedTask(true);
    let itemToEdit = {
      ...item
    };
    if (!itemToEdit?.task?.userCanComplete || itemToEdit?.task?.userCanComplete === 'false' || itemToEdit?.task?.userCanComplete === false) {
      setDisableAssigneesField(false);
    } else {
      itemToEdit.task.assigneeRoles = [];
      setDisableAssigneesField(true);
    }
    if (itemToEdit?.task?.assigneeRoles?.length > 0 && assigneeRoles.length > 0) {
      itemToEdit.task.assigneeRoles = itemToEdit.task.assigneeRoles.map((role) => {
        let populatedRole = assigneeRoles.find((aRole) => {
          return aRole?._id?.toString() === role?.toString();
        });
        if (populatedRole?._id) {
          role = populatedRole;
        }
        return role;
      });
    }
    setSelectedMilestone({ milestone: newMilestone });
    setSelectedTask(itemToEdit);
    setTimeout(() => {
      setSettingNewSelectedTask(false);
    });
    setSubmitError(null);
  };

  const confirmDelete = async(event, exitValue) => {
    if (exitValue === true) {
      const newState = [...milestones];
      newState[selectedTask.milestoneIndex].tasks.splice(selectedTask.taskIndex, 1);
      // setItems(
      //   newState.filter(group => group.length)
      // );
      setMilestones(newState);
      setSelectedTask({
        task: newTask
      });
      setTimeout(() => {
        setDeleteModalShow(false);
        setSubmitError(null);
      });

    } else {
      setDeleteModalShow(false);
    }
  };

  const confirmMilestoneDelete = async(event, exitValue) => {
    if (exitValue === true) {
      const newState = [...milestones];
      newState.splice(selectedMilestone.milestoneIndex, 1);
      setMilestones(newState);
      setSelectedMilestone({
        milestone: newMilestone
      });
      setTimeout(() => {
        setDeleteMilestoneModalShow(false);
        setSubmitError(null);
      });

    } else {
      setDeleteMilestoneModalShow(false);
    }
  };

  const deleteTask = () => {
    setDeleteModalShow(true);
  };

  const handleNewAssigneeRole = (role) => {
    if (role?.inputValue && role?.name) {
      let newAssigneeRoles = [...assigneeRoles];
      newAssigneeRoles.push(role);
      setAssigneeRoles(newAssigneeRoles);
      setSubmitError(null);
    }
  };

  const addNewAssigneeRole = () => {
    setAssigneeRoles((prev) => {
      return [...prev, { inputValue: '', name: '' }];
    });
  };

  const removeAssigneeRole = (role, roleIndex) => {
    let roleInUse = false;
    if (milestones?.length > 0) {
      milestones.forEach((milestone) => {
        if (milestone?.tasks?.length > 0) {
          milestone.tasks.forEach((task) => {
            if (task?.assigneeRoles?.length > 0) {
              task.assigneeRoles.forEach((taskRole) => {
                if (task.userCanComplete === false || task.userCanComplete === 'false') {
                  if (taskRole === role._id) {
                    roleInUse = true;
                  } else if (role.name === taskRole?.name) {
                    roleInUse = true;
                  }
                }
              });
            }
          });
        }
      });
    }
    if (roleInUse) {
      setRoleToDelete(role);
      setShowDeleteAssigneeRoleError(`This role can't be deleted yet, because it's being used.`);
    } else {
      setAssigneeRoles((prev) => {
        return prev.filter((item, index) => index !== roleIndex);
      });
      setRemovedAssigneeRoles([...removedAssigneeRoles, role]);
    }
  };

  const randomNumber = () => uuidv4();
  const editAssigneeRole = (role) => {
    let newAssigneeRoles = [...assigneeRoles];
    let roleIndex = role.index;
    newAssigneeRoles[roleIndex].name = role.name;
    newAssigneeRoles[roleIndex].inputValue = role.name;
    newAssigneeRoles[roleIndex].title = role.name;
    if (!role._id) {
      newAssigneeRoles[roleIndex]._id = randomNumber();
      newAssigneeRoles[roleIndex].tempId = true;
    }
    newAssigneeRoles[roleIndex].nameChange = true;
    setAssigneeRoles(newAssigneeRoles);
  };

  const onSelectedMilestoneChange = (milestone, index) => {
    setSelectedMilestone({ milestone: milestone, milestoneIndex: index });
    setSelectedTask({ task: newTask });
    setMilestoneFormValues(milestone);
    setSubmitError(null);
  };

  const deleteMilestone = () => {
    setDeleteMilestoneModalShow(true);
  };

  function validate(values) {
    let errors = null;
    if (!values.name) {
      errors = errors || {};
      errors.name = "Workflow name is required.";
      setSubmitError(errors.name);
    } else if (!values.status) {
      errors = errors || {};
      errors.status = "Workflow status is required.";
      setSubmitError(errors.name);
    }
    return errors;
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row", flex: 1, width: "calc(100% + 30px)", marginTop: '30px' }}>
        <div style={{ display: "flex", flexDirection: "column", flex: 2, marginRight: "40px" }}>
          <Row>
            <Col>
              <Formik
                validate={validate}
                validateOnChange
                validateOnBlur
                initialValues={workflow}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, setFieldValue }) => {
                  setSubmitting(true);
                  handleSubmit(values, setFieldValue);
                }}
              >
                {({ touched, errors, isSubmitting, setFieldValue, ...props }) => (
                  <Form noValidate ref={formRef}>
                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', flex: 2 }}>
                          <label className={'workflow-view-label'} htmlFor={'name'}>
                            {'Workflow Name'}
                            {" *"}
                          </label>
                          <Field
                            type={'text'}
                            name={'name'}
                            disabled={false}
                            onKeyUp={handleChange}
                            placeholder={'Workflow Name'}
                            className={`form-control`}
                          />
                          <ErrorMessage
                            component="div"
                            name={'name'}
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', flex: 1, margin: '0 10px' }}>
                          <label className={'workflow-view-label'} htmlFor={'status'}>
                            {'Status'}
                            {" *"}
                          </label>
                          <Field
                            as={'select'}
                            name={'status'}
                            disabled={false}
                            onKeyUp={handleChange}
                            placeholder={'Status'}
                            className={`form-control`}
                          >
                            <option value={'Draft'}>Draft</option>
                            <option value={'Active'}>Active</option>
                            <option value={'Archived'}>Archived</option>
                          </Field>
                          <ErrorMessage
                            component="div"
                            name={'status'}
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', flex: 2, marginRight: '10px' }}>
                          <label className={'workflow-view-label'} htmlFor={'description'}>
                            {'Description'}
                          </label>
                          <Field
                            type={'textarea'}
                            name={'description'}
                            disabled={false}
                            onKeyUp={handleChange}
                            placeholder={'Description'}
                            className={`form-control`}
                          />
                          <ErrorMessage
                            component="div"
                            name={'description'}
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>

                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', flex: 2, marginRight: '10px' }}>
                        <label className={'workflow-view-label'} htmlFor={'assigneeRoles'}>
                          {'Role(s)'}
                        </label>
                        {assigneeRoles?.length > 0 && assigneeRoles.map((role, roleIndex) => {
                          return (
                            <div key={`editableAssigneeRole-${roleIndex}`} style={{ display: 'flex', flexDirection: 'row', marginBottom: '5px' }}>
                              <div style={{ display: 'flex', flex: '1', marginRight: '10px' }}>
                                <Field
                                  type={'text'}
                                  value={role.name}
                                  disabled={false}
                                  onChange={(e) => {
                                    editAssigneeRole({
                                      ...role,
                                      name: e.target.value,
                                      index: roleIndex
                                    });
                                  }}
                                  placeholder={'Role'}
                                  className={`form-control`}
                                />
                              </div>
                              <div style={{ display: 'flex', marginBottom: '5px' }}></div>
                              <button
                                className='btn btn-outline-secondary'
                                style={{ marginRight: '10px' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  removeAssigneeRole(role, roleIndex);
                                }}
                              >
                                <FaRegTrashAlt style={{ verticalAlign: "middle", marginRight: '5px', marginTop: '-5px' }} />
                                <span style={{ verticalAlign: "middle" }}>
                                  Delete
                                </span>
                              </button>
                            </div>
                          );
                        })}
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                          <button
                            className='btn btn-outline-secondary'
                            style={{ marginRight: '10px' }}
                            onClick={(e) => {
                              e.preventDefault();
                              addNewAssigneeRole();
                            }}
                          >
                            <span style={{ verticalAlign: "middle", marginRight: '5px', marginTop: '-5px' }} >
                              +
                            </span>
                            <span style={{ verticalAlign: "middle" }}>
                              Add Role
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {milestones?.length > 0 && (
                      <>
                        <br></br>
                        <br></br>
                        <SortableMilestones items={milestones} onChange={onMilestonesChange} onSelectedItemChange={onSelectedItemChange} onSelectedMilestoneChange={onSelectedMilestoneChange} assigneeRoles={assigneeRoles} />
                      </>
                    )}

                    {submitError && <Alert variant={"danger"}>{submitError}</Alert>}
                    {submitSuccess && (
                      <Alert variant={"success"}>{submitSuccess}</Alert>
                    )}

                    <div className="form-group">
                      {submittingForm ? (
                        <Spinner animation="border" role="status">
                          <span className="sr-only">Loading...</span>
                        </Spinner>
                      ) : (
                        <>
                          <div className='hide'>{JSON.stringify(errors)}<br></br></div>
                          <button
                            className={`mb-2 btn btn-outline-secondary ${submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                              ? "hide"
                              : ""
                            }`}
                            onClick={handleCancel}
                            style={{ marginRight: "5px" }}
                            disabled={
                              submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                            }
                          >
                        Cancel
                          </button>
                          <button
                            type="submit"
                            onClick={() => {
                              handleSubmit(props.values, setFieldValue);
                            }}
                            className={`mb-2 btn btn-outline-primary ${submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                              ? "hide"
                              : ""
                            }`}
                            disabled={
                              submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                            }
                          >
                        Save
                          </button>
                        </>
                      )
                      }
                    </div >
                  </Form >
                )}
              </Formik >
            </Col>
          </Row>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0%',
          paddingLeft: '40px',
          paddingRight: '40px',
          borderLeft: '1px solid #ccc',
          position: 'sticky',
          alignSelf: 'flex-start',
          top: '-20px',
          height: 'calc(100vh - 132px)',
          overflow: 'hidden',
          overflowY: 'auto',
        }}>
          {selectedTask?.milestoneIndex >= 0 && (
            <Formik
              enableReinitialize={true}
              validateOnChange
              validateOnBlur
              initialValues={formValues}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting, setFieldValue }) => {
                setSubmitting(true);
                handleTaskSubmit(values, setFieldValue);
              }}
            >
              {({ touched, errors, isSubmitting, setFieldValue, ...props }) => (
                <>
                  <b>Task Details</b>
                  <br></br>
                  <Form noValidate ref={taskFormRef}>
                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'name'}>
                        {'Task Name'}
                        {" *"}
                      </label>
                      <Field
                        type={'text'}
                        name={'name'}
                        disabled={false}
                        onKeyUp={handleTaskChange}
                        placeholder={''}
                        className={`form-control`}
                      />
                      <ErrorMessage
                        component="div"
                        name={'name'}
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'type'}>
                        {'Task Type'}
                        {" *"}
                      </label>
                      <Field
                        as="select"
                        name={'type'}
                        disabled={false}
                        onChange={(e) => {
                          handleTaskChange(e);
                          setFieldValue('type', e?.target?.value);
                        }}
                        className={`form-control`}
                      >
                        <option value="message">Message</option>
                        <option value="simple">Simple (default)</option>
                        <option value="upload">Upload</option>
                      </Field>
                      <ErrorMessage
                        component="div"
                        name={'type'}
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'details'}>
                        {'Details'}
                      </label>
                      <FormattedTextEditor
                        context={selectedTask.task}
                        setFieldValue={(val) => setFieldValue("details", val)}
                        value={props.values.details}
                        name={'details'}
                        onChange={handleTaskChange}
                      />
                      <ErrorMessage
                        component="div"
                        name={'details'}
                        className="invalid-feedback"
                      />
                    </div>
                    {
                      props.values.type !== 'message' && (
                        <>
                          <div className="form-group">
                            <label className={'workflow-view-label'} htmlFor={'instructions'}>
                              {'Instructions'}
                            </label>
                            <FormattedTextEditor
                              context={selectedTask.task}
                              setFieldValue={(val) => setFieldValue("instructions", val)}
                              value={props.values.instructions}
                              name={'instructions'}
                              onChange={handleTaskChange}
                            />
                            <ErrorMessage
                              component="div"
                              name={'instructions'}
                              className="invalid-feedback"
                            />
                          </div>
                          <div className="form-group">
                            <label className={'workflow-view-label'} htmlFor={'userCanComplete'}>
                              {'Completion'}
                              {" *"}
                            </label>
                            <Field
                              as={'select'}
                              name={'userCanComplete'}
                              disabled={false}
                              onChange={(e) => {
                                handleTaskChange(e);
                                setFieldValue("userCanComplete", e?.target?.value);
                                if (selectedTask?.task?.draggableId) {
                                  if (e?.target?.value === true || e?.target?.value === 'true') {
                                    setDisableAssigneesField(true);
                                    setTimeout(() => {
                                      setFieldValue("assigneeRoles", []);
                                      props.values['assigneeRoles'] = [];
                                      onSelectedItemChange({
                                        ...selectedTask,
                                        task: {
                                          ...selectedTask.task,
                                          assigneeRoles: []
                                        }
                                      });
                                      errors.assigneeRoles = null;
                                    });
                                  } else {
                                    setDisableAssigneesField(false);
                                    validationSchema.assigneeRoles = Yup.array()
                                      .of(Yup.object({ name: Yup.string(), _id: Yup.string() }))
                                      .min(1, `You need to assign at least one role`)
                                      .required('Required');

                                  }
                                } else {
                                  setDisableAssigneesField(true);
                                }
                              }}
                              placeholder={'Completion'}
                              className={`form-control`}
                            >
                              <option value={false}>Assignee</option>
                              <option defaultValue={true} value={true}>User</option>
                            </Field>
                            <ErrorMessage
                              component="div"
                              name={'userCanComplete'}
                              className="invalid-feedback"
                            />
                          </div>
                          <div className={!props.values.userCanComplete || props.values.userCanComplete === false || props.values.userCanComplete === 'false' && errors?.assigneeRoles?.length > 0 ? 'is-invalid' : ''} style={{ height: 'auto' }}>
                            <label className={'workflow-view-label'} htmlFor={'userCanComplete'}>
                              {'Assignees'}
                              {!props.values.userCanComplete || props.values.userCanComplete === false || props.values.userCanComplete === 'false' ? " *" : ""}
                            </label>
                            {!settingNewSelectedTask ? (
                              <MultiSelectTypeahead
                                disabled={disableAssigneesField}
                                name={'assigneeRoles'}
                                displayName={'Assignees'}
                                items={assigneeRoles}
                                allowNewItems={false}
                                onNewItemAdded={handleNewAssigneeRole}
                                preventSelectAll={true}
                                dynamicHeight={true}
                                resetInputAfterSelectOrRemove={true}
                                disableCloseOnSelect={true}
                                showItemsOnFocus={true}
                                placeholder="Search assignee roles"
                                selectAllLabel="Select all"
                                defaultValue={props.values['assigneeRoles']}
                                className={`form-control`}
                                onChange={(value) => {
                                  let newAssignees = [...assigneeRoles, ...value];
                                  const uniqueArray = newAssignees.filter((value, index) => {
                                    const _value = JSON.stringify(value);
                                    return index === newAssignees.findIndex(obj => {
                                      return JSON.stringify(obj) === _value;
                                    });
                                  });

                                  setAssigneeRoles(uniqueArray);
                                  setFieldValue('assigneeRoles', value);
                                  handleTaskChange({ target: { value: value, name: 'assigneeRoles' }});
                                }}
                                onFocus={() => {
                                  touched['assigneeRoles'] = true;
                                  if (!props?.values?.assigneeRoles?.length || props?.values?.assigneeRoles?.length === 0) {
                                    errors['assigneeRoles'] = 'A minimum of 1 assignee is required';
                                  } else {
                                    delete errors['assigneeRoles'];
                                  }
                                  props.validateForm();
                                }}
                                onBlur={() => {
                                  touched['assigneeRoles'] = true;
                                  if (!props?.values?.assigneeRoles?.length || props?.values?.assigneeRoles?.length === 0) {
                                    errors['assigneeRoles'] = 'A minimum of 1 assignee is required';
                                  } else {
                                    delete errors['assigneeRoles'];
                                  }
                                  props.validateForm();
                                }}
                              />
                            ) : (
                              <Field
                                type={'text'}
                                name={'assigneeRolesTemp'}
                                disabled={false}
                                onKeyUp={null}
                                placeholder={''}
                                className={`form-control`}
                              />
                            )}
                          </div>
                          <ErrorMessage
                            component="div"
                            name={'assigneeRoles'}
                            className="invalid-feedback"
                          />
                        </>
                      )
                    }


                    <div style={{ width: '100%', textAlign: 'center', marginTop: '20px' }}>
                      <button
                        className='btn btn-outline-secondary'
                        style={{ borderColor: selectedTask?.milestoneIndex >= 0 && selectedTask?.taskIndex >= 0 ? '#000' : '#6c757d' }}
                        type="button"
                        disabled={selectedTask?.milestoneIndex >= 0 && selectedTask?.taskIndex >= 0 ? false : true}
                        onClick={() => {
                          deleteTask();
                        }}
                      >
                        <FaRegTrashAlt style={{ verticalAlign: "middle", marginRight: '5px', marginTop: '-5px' }} />
                        <span style={{ verticalAlign: "middle" }}>
                          Delete Task
                        </span>
                      </button>
                    </div>
                  </Form >
                </>
              )}
            </Formik >
          )}
          {selectedMilestone?.milestoneIndex >= 0 && (
            <Formik
              enableReinitialize={true}
              validateOnChange
              validateOnBlur
              initialValues={milestoneFormValues}
              validationSchema={milestoneValidationSchema}
              onSubmit={(values, { setSubmitting, setFieldValue }) => {
                setSubmitting(true);
                handleTaskSubmit(values, setFieldValue);
              }}
            >
              {({ touched, errors, isSubmitting, setFieldValue, ...props }) => (
                <>
                  <b>Milestone Details</b>
                  <br></br>
                  <Form noValidate ref={milestoneFormRef}>
                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'name'}>
                        {'Milestone Name'}
                        {" *"}
                      </label>
                      <Field
                        type={'text'}
                        name={'name'}
                        disabled={false}
                        onKeyUp={handleMilestoneChange}
                        placeholder={''}
                        className={`form-control`}
                      />
                      <ErrorMessage
                        component="div"
                        name={'name'}
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'startDay'}>
                        {'Start Day'}
                        {" *"}
                      </label>
                      <Field
                        type={'number'}
                        name={'startDay'}
                        disabled={false}
                        onKeyUp={handleMilestoneChange}
                        placeholder={''}
                        className={`form-control`}
                      />
                      <ErrorMessage
                        component="div"
                        name={'startDay'}
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="form-group">
                      <label className={'workflow-view-label'} htmlFor={'endDay'}>
                        {'End Day'}
                        {" *"}
                      </label>
                      <Field
                        type={'number'}
                        name={'endDay'}
                        disabled={false}
                        onKeyUp={handleMilestoneChange}
                        placeholder={''}
                        className={`form-control`}
                      />
                      <ErrorMessage
                        component="div"
                        name={'endDay'}
                        className="invalid-feedback"
                      />
                    </div>

                    <div style={{ width: '100%', textAlign: 'center', marginTop: '20px' }}>
                      <button
                        className='btn btn-outline-secondary'
                        style={{ borderColor: selectedMilestone?.milestoneIndex >= 0 ? '#000' : '#6c757d' }}
                        type="button"
                        disabled={selectedMilestone?.milestoneIndex >= 0 ? false : true}
                        onClick={() => {
                          deleteMilestone();
                        }}
                      >
                        <FaRegTrashAlt style={{ verticalAlign: "middle", marginRight: '5px', marginTop: '-5px' }} />
                        <span style={{ verticalAlign: "middle" }}>
                        Delete Milestone
                        </span>
                      </button>
                    </div>
                  </Form >
                </>
              )}
            </Formik >
          )}
        </div>
      </div>
      <ConfirmationModal
        title={
          "Delete Task"
        }
        body={
          <>
            <p>
              {`This will delete all associated data and can't be undone. Do you want to proceed?`}
            </p>
          </>
        }
        show={deleteModalShow}
        confirmtext={"Delete"}
        onHide={(event, exitValue) => {
          confirmDelete(event, exitValue);
        }}
      />
      <ConfirmationModal
        title={
          "Delete Milestone"
        }
        body={
          <>
            <p>
              {`This will delete all associated data and can't be undone. Do you want to proceed?`}
            </p>
          </>
        }
        show={deleteMilestoneModalShow}
        confirmtext={"Delete"}
        onHide={(event, exitValue) => {
          confirmMilestoneDelete(event, exitValue);
        }}
      />
      <ErrorModal
        show={showDeleteAssigneeRoleError}
        onHide={() => {
          setRoleToDelete(null);
          setShowDeleteAssigneeRoleError(null);
        }}
        title={roleToDelete?.name || 'Action not allowed'}
        body={showDeleteAssigneeRoleError}
        confirmtext="OK"
      />
    </>
  );
}